import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Grid,
  TextField,
  Typography,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  IGenericType,
  IPolygonData,
  IPolygonInput,
  IPolygonSoilDataInput,
  IProduce,
} from 'constants/interfaces';
import {
  apiGetProduce,
  apiUpdateFarmField,
  apiGetIrrigationTypes,
} from 'constants/Api';
// eslint-disable-next-line max-len
import {AddNewFieldSoilData} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddNewFieldSoilData';

interface IUpdateFieldFormProps {
  onClose: () => void;
  fieldData: IPolygonData;
  farmId: number;
  onSuccess?: () => void;
  /** Default buyer when field has none (e.g. from overview) */
  initialBuyer?: string;
  /** Default funder when field has none (e.g. from overview) */
  initialFunder?: string;
}

export const UpdateFieldForm: React.FC<IUpdateFieldFormProps> = ({
  onClose,
  fieldData,
  farmId,
  onSuccess,
  initialBuyer = 'Pepsico',
  initialFunder = 'ABSA',
}) => {
  // Full produce list and variety
  const [produce, setProduce] = useState<IProduce[]>([]);
  const [varieties, setVarieties] = useState<IGenericType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Step toggle for soil data
  const [soilDataStep, setSoilDataStep] = useState(false);

  const [irrigationOptions, setIrrigationOptions] = useState<string[]>([
    'None',
  ]);

  // Convert API soil data (null allowed) to input type (undefined only)
  const toSoilDataInput = (
    raw: typeof fieldData.soilData,
  ): IPolygonSoilDataInput | null => {
    if (!raw) return null;
    return {
      soilType: raw.soilType ?? undefined,
      pH: raw.pH,
      P: raw.P,
      K: raw.K,
      Ca: raw.Ca,
      Mg: raw.Mg,
      Na: raw.Na,
      cations: raw.cations,
      exchangeableAcidity: raw.exchangeableAcidity,
    };
  };

  // The soil data (if any) that the user has added
  const [soilData, setSoilData] = useState<IPolygonSoilDataInput | null>(() =>
    toSoilDataInput(fieldData?.soilData),
  );

  // Format date from various formats to YYYY-MM-DD for input
  const formatDateForInput = (dateVal: any): string => {
    if (!dateVal) return '';
    // If it's already in YYYY-MM-DD format
    if (typeof dateVal === 'string' && dateVal.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateVal;
    }
    // Try to parse (handles ISO strings, timestamps as numbers, etc.)
    const asNum = Number(dateVal);
    const date =
      !isNaN(asNum) && dateVal !== '' ? new Date(asNum) : new Date(dateVal);

    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  // Initialize form with existing field data (crop/variety as string for dropdown match)
  const [form, setForm] = useState({
    name: fieldData?.info?.fieldName || '',
    crop:
      fieldData?.info?.produce?.id != null
        ? String(fieldData.info.produce.id)
        : '',
    variety: '', // Set in useEffect after varieties load
    plantingDate: formatDateForInput(
      (fieldData?.info as any)?.plantingDate ||
        (fieldData?.info as any)?.plantedOn ||
        undefined,
    ),
    population: fieldData?.info?.plantPopulation?.toString() || '',
    irrigation: fieldData?.info?.irrigation ?? '',
    yield: fieldData?.info?.expectedYield?.toString() || '',
    buyer: (fieldData as any)?.buyer ?? initialBuyer ?? '',
    funder: (fieldData as any)?.funder ?? initialFunder ?? '',
  });

  const [agronomyForm, setAgronomyForm] = useState({
    plannedPlantingDate: '',
    rowSpacingCm: '',
    seedSpacingCm: '',
    plantingDepthCm: '',
    harvestStartDate: '',
    harvestEndDate: '',
  });

  useEffect(() => {
    // Fetch production data
    apiGetProduce().then((res) => {
      setProduce(res);
      const produceId = fieldData?.info?.produce?.id;
      const varietyName = fieldData?.info?.variety;
      if (produceId != null) {
        const idStr = String(produceId);
        const existingProduce = res.find((p) => String(p.id) === idStr);
        if (existingProduce) {
          const vars = existingProduce.variety || [];
          setVarieties(vars);
          setForm((prev) => ({...prev, crop: String(existingProduce.id)}));
          if (varietyName && vars.length) {
            const varietyMatch = vars.find(
              (v: IGenericType) => v.name === varietyName,
            );
            if (varietyMatch) {
              setForm((prev) => ({
                ...prev,
                variety: String(varietyMatch.id),
              }));
            }
          }
        }
      }
    });

    apiGetIrrigationTypes()
      .then((res) => {
        setIrrigationOptions([
          'None',
          ...res.map((r) => (r.name ? String(r.name) : '')),
        ]);
      })
      .catch(console.error);

    // Initialize agronomy fields from fieldData.info
    if (fieldData?.info) {
      const info = fieldData.info as any;
      setAgronomyForm({
        plannedPlantingDate: formatDateForInput(info.plannedPlantingDate),
        rowSpacingCm: info.rowSpacingCm?.toString() || '',
        seedSpacingCm: info.seedSpacingCm?.toString() || '',
        plantingDepthCm: info.plantingDepthCm?.toString() || '',
        harvestStartDate: formatDateForInput(info.harvestStartDate),
        harvestEndDate: formatDateForInput(info.harvestEndDate),
      });

      // Also ensure form dates are updated if fieldData changes
      setForm((prev) => ({
        ...prev,
        plantingDate: formatDateForInput(info.plantingDate || info.plantedOn),
      }));
    }
  }, [fieldData, farmId]);

  // ========== HANDLERS FOR PRODUCE / VARIETY ==========
  const handleProduceChange = (e: React.ChangeEvent<{value: unknown}>) => {
    // Find the produce by ID
    const selected = produce.find((p) => p.id === e.target.value);
    setVarieties(selected?.variety || []);

    // Store the ID in form.crop (so MUI can match the <MenuItem value>)
    setForm((prev) => ({...prev, crop: String(selected?.id || '')}));
  };

  const handleVarietyChange = (e: React.ChangeEvent<{value: unknown}>) => {
    // Find the variety by ID
    const found = varieties.find((v) => v.id === e.target.value);
    // Store the ID in form.variety
    setForm((prev) => ({...prev, variety: String(found?.id || '')}));
  };

  // For other text fields & string-based selects (e.g. irrigation)
  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({...prev, [field]: e.target.value}));
    };

  const handleAgronomyChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setAgronomyForm((prev) => ({...prev, [field]: e.target.value}));
    };

  // Basic required check
  const isFormValid = (): boolean => {
    const {
      name,
      crop,
      plantingDate,
      population,
      irrigation,
      yield: yieldVal,
    } = form;

    return String(name).trim() &&
      String(crop).trim() &&
      String(plantingDate).trim() &&
      String(population).trim() &&
      String(irrigation).trim() &&
      String(yieldVal).trim()
      ? true
      : false;
  };

  // Update field to backend
  const handleUpdateField = async () => {
    if (!isFormValid() || !fieldData?.id) return;

    setIsLoading(true);

    // If you need the actual produceId:
    const selectedProd = produce.find(
      (p) => String(p.id) === String(form.crop),
    );
    const produceId = selectedProd?.id || form.crop;

    // If you need the variety name or ID:
    const varietyObj = varieties.find(
      (v) => String(v.id) === String(form.variety),
    );
    const varietyName = varietyObj?.name || '';

    const polygonData: IPolygonInput = {
      info: {
        fieldName: form.name,
        hectares: fieldData?.info?.hectares || 0,
        produceId: produceId,
        variety: varietyName,
        plantedOn: form.plantingDate,
        plantPopulation: parseFloat(form.population),
        irrigation: form.irrigation,
        expectedYield: parseFloat(form.yield),
        plantingDate: form.plantingDate,
        plannedPlantingDate: agronomyForm.plannedPlantingDate,
        rowSpacingCm: parseFloat(agronomyForm.rowSpacingCm) || 0,
        seedSpacingCm: parseFloat(agronomyForm.seedSpacingCm) || 0,
        plantingDepthCm: parseFloat(agronomyForm.plantingDepthCm) || 0,
        harvestStartDate: agronomyForm.harvestStartDate,
        harvestEndDate: agronomyForm.harvestEndDate,
      },
      coordinates: fieldData.coordinates || [],
      soilData: soilData
        ? {
            soilType: soilData.soilType,
            pH: parseFloat(soilData.pH?.toString() || '0'),
            P: parseFloat(soilData.P?.toString() || '0'),
            K: parseFloat(soilData.K?.toString() || '0'),
            Ca: parseFloat(soilData.Ca?.toString() || '0'),
            Mg: parseFloat(soilData.Mg?.toString() || '0'),
            Na: parseFloat(soilData.Na?.toString() || '0'),
            cations: parseFloat(soilData.cations?.toString() || '0'),
            exchangeableAcidity: parseFloat(
              soilData.exchangeableAcidity?.toString() || '0',
            ),
          }
        : undefined,
    };

    try {
      console.log('Updating farm field info:', polygonData);
      const fieldRes: any = await apiUpdateFarmField(fieldData.id, polygonData);
      console.log('Update field response:', fieldRes);

      if (fieldRes && fieldRes.success === false) {
        throw new Error(fieldRes.message || 'Error updating farm field info');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Detailed Error updating field:', err);
      let errorMessage = 'Failed to update field.';
      if (Array.isArray(err)) {
        errorMessage = err.join(', ');
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      alert(`Failed to update field: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setForm({
      name: fieldData?.info?.fieldName || '',
      crop:
        fieldData?.info?.produce?.id != null
          ? String(fieldData.info.produce.id)
          : '',
      variety: '',
      plantingDate: formatDateForInput(
        (fieldData?.info as any)?.plantedOn || undefined,
      ),
      population: fieldData?.info?.plantPopulation?.toString() || '',
      irrigation: fieldData?.info?.irrigation ?? '',
      yield: fieldData?.info?.expectedYield?.toString() || '',
      buyer: (fieldData as any)?.buyer ?? initialBuyer ?? '',
      funder: (fieldData as any)?.funder ?? initialFunder ?? '',
    });
    setAgronomyForm({
      plannedPlantingDate: '',
      rowSpacingCm: '',
      seedSpacingCm: '',
      plantingDepthCm: '',
      harvestStartDate: '',
      harvestEndDate: '',
    });
    setSoilData(toSoilDataInput(fieldData?.soilData));
  };

  if (soilDataStep) {
    return (
      <AddNewFieldSoilData
        initialSoilData={soilData || {}}
        onSubmit={(data) => {
          setSoilData({
            soilType: data.soilType,
            pH: data.pH,
            P: data.P,
            K: data.K,
            Ca: data.Ca,
            Mg: data.Mg,
            Na: data.Na,
            cations: data.cations,
            exchangeableAcidity: data.exchangeableAcidity,
          });
          setSoilDataStep(false);
        }}
        onCancel={() => setSoilDataStep(false)}
      />
    );
  }

  const fieldConfigs = [
    {label: 'Field name', key: 'name', placeholder: 'Enter name'},
    {
      label: 'Crop planted',
      key: 'crop',
      placeholder: 'Select crop',
      select: true,
      options: produce,
      onChange: handleProduceChange,
    },
    {
      label: (
        <Box display="flex" alignItems="center">
          Variety planted
          <Tooltip title="Type of crop variety">
            <HelpOutlineIcon fontSize="small" style={{marginLeft: 4}} />
          </Tooltip>
        </Box>
      ),
      key: 'variety',
      placeholder: 'Select variety',
      select: true,
      options: varieties,
      onChange: handleVarietyChange,
    },
    {label: 'Planting date', key: 'plantingDate', type: 'date'},
    {
      label: 'Plant population',
      key: 'population',
      placeholder: 'Enter population',
    },
    {
      label: 'Irrigation',
      key: 'irrigation',
      select: true,
      options: irrigationOptions,
    },
    {label: 'Expected yield', key: 'yield', placeholder: 'Enter yield'},
    {
      label: 'Buyer',
      key: 'buyer',
      select: true,
      options: ['Pepsico', 'ABSA'],
    },
    {
      label: 'Funder',
      key: 'funder',
      select: true,
      options: ['ABSA', 'Pepsico'],
    },
    // Agronomy Fields
    {
      label: 'Planned planting date',
      key: 'plannedPlantingDate',
      type: 'date',
      isAgronomy: true,
    },
    {
      label: 'Row spacing (cm)',
      key: 'rowSpacingCm',
      placeholder: 'cm',
      isAgronomy: true,
      halfWidth: true,
    },
    {
      label: 'Seed spacing (cm)',
      key: 'seedSpacingCm',
      placeholder: 'cm',
      isAgronomy: true,
      halfWidth: true,
    },
    {
      label: 'Planting depth (cm)',
      key: 'plantingDepthCm',
      placeholder: 'cm',
      isAgronomy: true,
    },
    {
      label: 'Harvest start date',
      key: 'harvestStartDate',
      type: 'date',
      isAgronomy: true,
      halfWidth: true,
    },
    {
      label: 'Harvest end date',
      key: 'harvestEndDate',
      type: 'date',
      isAgronomy: true,
      halfWidth: true,
    },
  ];

  return (
    <Box
      p={3}
      borderRadius={12}
      boxShadow={1}
      bgcolor="#FAFAFA"
      maxWidth={800}
      margin="auto"
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
        paddingBottom: 24,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" style={{fontWeight: 600}}>
          Update field
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {fieldConfigs.map(
          ({
            label,
            key,
            placeholder,
            type,
            select,
            options,
            onChange,
            isAgronomy,
            halfWidth,
          }: any) => (
            <Grid item xs={12} sm={halfWidth ? 6 : 12} key={key}>
              <Box mb={1}>
                <Typography
                  style={{fontSize: 14, fontWeight: 500, marginBottom: 4}}
                >
                  {label}
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder={placeholder}
                  type={type || 'text'}
                  value={
                    isAgronomy
                      ? (agronomyForm as any)[key] ?? ''
                      : (form as any)[key] ?? ''
                  }
                  onChange={
                    onChange ||
                    (isAgronomy ? handleAgronomyChange(key) : handleChange(key))
                  }
                  fullWidth
                  size="small"
                  select={Boolean(select)}
                  InputProps={{
                    style: {backgroundColor: '#fff', borderRadius: 8},
                  }}
                  disabled={isLoading}
                  InputLabelProps={type === 'date' ? {shrink: true} : undefined}
                >
                  {select &&
                    options?.map((opt: any) => {
                      if (opt.id) {
                        return (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        );
                      }
                      return (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      );
                    })}
                </TextField>
              </Box>
            </Grid>
          ),
        )}
      </Grid>

      <Box
        mt={4}
        pt={2}
        borderTop="1px solid #e0e0e0"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          onClick={handleClear}
          style={{color: '#2D9B73', fontWeight: 600}}
          disabled={isLoading}
        >
          Clear All
        </Button>
        <Box display="flex" gridColumnGap={8}>
          <Button
            onClick={() => setSoilDataStep(true)}
            variant="outlined"
            style={{
              borderRadius: 24,
              padding: '6px 18px',
              borderColor: '#ccc',
              backgroundColor: '#f9f9f9',
            }}
            disabled={isLoading}
          >
            Add soil data
          </Button>
          <Button
            onClick={handleUpdateField}
            variant="contained"
            style={{
              borderRadius: 24,
              padding: '6px 18px',
              backgroundColor: isFormValid() && !isLoading ? '#2D9B73' : '#ccc',
              color: '#fff',
              fontWeight: 600,
            }}
            disabled={!isFormValid() || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
